import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { FormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Router, RouterLink } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  ColumnMode,
  DatatableComponent as NgxDatatableComponent,
  NgxDatatableModule
} from "@swimlane/ngx-datatable";
import { version as versionApp } from "../../../../../../package.json";
import { DatalistActionItem } from "../../models/datalist-action-item.interface";
import { PermissionService } from "../../services/auth/permission.service";
import { LocalStorageService } from "../../services/local-storage.service";

@Component({
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    CommonModule,
    FormsModule,
    MatInput,
    MatFormField,
    NgxDatatableModule,
    RouterLink,
    TranslateModule
  ],
  selector: "app-datatable",
  standalone: true,
  styleUrls: ["./datatable.component.scss"],
  templateUrl: "./datatable.component.html"
})
export class DatatableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild(NgxDatatableComponent) private ngxTable: NgxDatatableComponent;
  private resizeObserver: ResizeObserver | undefined;
  private lastObservedWidth: number = 0;

  rows: any[] = [];
  columnList: any[] = [];
  columnMapping: any[] = [];
  loadingIndicator = true;
  isEditable: any = {};
  editedItemOriginalData: any[] = [];
  showColumnSettings: boolean = false;
  sorts: any[] = [];

  ColumnMode = ColumnMode;

  @Input() enableExternalSorting: boolean = true;
  @Input() enableSearch: boolean = false;
  @Input() columns: { prop: string; name: string; width?: number; maxWidth?: number; sortable?: boolean }[];
  @Input() tableIdentifier: string = null;
  @Input() data: any;
  @Input() actions: DatalistActionItem[];

  @Input() buttonTitle: any;
  @Input() placeholder: any;
  @Input() disabled: any;
  @Input() showColumnConfig: boolean = true;
  // Render the toolbar row (and the [dtToolbarActions] slot) even when neither
  // search nor the column-config gear are shown — e.g. to keep a projected
  // Add button right-aligned where the gear used to be.
  @Input() showToolbarActions: boolean = false;
  // Horizontal scrollbar. Defaults to true to preserve wide-table behavior;
  // set false on narrow tables that fit so no needless horizontal scroll shows.
  @Input() scrollbarH: boolean = true;
  @Input() footerHeight: number = 50;
  @Output() search: EventEmitter<any> = new EventEmitter<any>();
  public searchText: string;
  @Output() callbackOnUpdate: EventEmitter<any> = new EventEmitter<any>();
  @Output() openCustomFieldsCallback: EventEmitter<any> = new EventEmitter<
    any
  >();
  @Output() callbackEmit = new EventEmitter();
  @Output() sortEvent = new EventEmitter();

  /* Memo for renderedColumnList — see its docblock for why this is cached rather than computed.
     Declared here, after the inputs, because member-ordering wants private fields last. `any`
     matches columnList, which this only ever filters; typing it would mean typing that too. */
  private renderedColumns: any[] = [];
  private renderedFrom: any[] | undefined;
  private renderedWithActions: boolean | undefined;

  constructor(
    public router: Router,
    public localStorage: LocalStorageService,
    public translate: TranslateService,
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    private permissionService: PermissionService
  ) {}

  version = versionApp;

  ngOnInit() {
    this.rows = this.data;
    //console.log(this.actions);
    this.setupColumns();
    this.setupSort();
    /*console.log(this.enableExternalSorting);
    console.log("ngOninit Coolumns: ");
    console.log(this.columns);*/
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.columnMapping,
      event.previousIndex,
      event.currentIndex
    );
    this.columnList = this.columnMapping.filter((c) => !c.disabled);
    this.persistColumns();
  }

  // Persist the current column order/visibility to localStorage (per table).
  private persistColumns(): void {
    this.localStorage.set(
      "tablecolumns_" + this.version + this.tableIdentifier,
      JSON.stringify(this.columnMapping)
    );
  }

  // True when every column is currently visible (drives the "Show all" toggle).
  public get allColumnsVisible(): boolean {
    return (
      this.columnMapping.length > 0 &&
      this.columnMapping.every((c: any): boolean => !c.disabled)
    );
  }

  // Show all / hide all columns at once.
  public toggleAllColumns(): void {
    const makeVisible: boolean = !this.allColumnsVisible;
    this.columnMapping = this.columnMapping.map((c: any): any => ({
      ...c,
      disabled: !makeVisible
    }));
    this.columnList = this.columnMapping.filter((c: any): boolean => !c.disabled);
    this.persistColumns();
  }

  // Reorder a column up (-1) or down (+1) via the arrow buttons.
  public moveColumn(index: number, direction: number): void {
    const target: number = index + direction;
    if (target < 0 || target >= this.columnMapping.length) {
      return;
    }
    moveItemInArray(this.columnMapping, index, target);
    this.columnList = this.columnMapping.filter((c: any): boolean => !c.disabled);
    this.persistColumns();
  }

  // Restore the original column order with every column visible.
  public resetColumns(): void {
    this.columnMapping = this.columns.map((col: any): any => ({
      ...col,
      disabled: false,
      sortable: col.sortable !== undefined ? col.sortable : true
    }));
    this.columnList = [...this.columnMapping];
    this.persistColumns();
  }

  searcher(ev: any) {
    this.search.emit(ev);
  }

  public noopComparator(): number {
    return 0;
  }

  ngOnChanges() {
    this.setupColumns();
  }

  // ngx-datatable only re-measures column widths on window:resize or when the
  // rows reference changes. When data arrives synchronously (e.g. a cached
  // observable emitting during ngOnInit), every internal recalculate() runs
  // before the surrounding layout settles (page scrollbar, card rendering),
  // freezing wrong widths. Re-run recalculate() whenever the container width
  // actually changes.
  public ngAfterViewInit(): void {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const width = entries[entries.length - 1].contentRect.width;
      if (width === this.lastObservedWidth) {
        return;
      }
      this.lastObservedWidth = width;
      this.zone.run((): void => this.ngxTable?.recalculate());
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  toggleColumnSettings() {
    this.showColumnSettings = !this.showColumnSettings;
  }

  setupColumns() {
    let columnMapping;
    //console.log(this.columns);
    if (!this.tableIdentifier) {
        this.columnList = this.columns.map(col => ({
            ...col,
            sortable: col.sortable !== undefined ? col.sortable : true // Default to true
        }));
        this.columnMapping = this.columnList;
    }
    if (!this.columns) {
        return;
    }
    let columnMappingString = this.localStorage.get(
        "tablecolumns_" + this.version + this.tableIdentifier
    );
    let oldColumns: any = [...this.columns];

    if (!columnMappingString) {
        columnMapping = this.columns.map(col => ({
            ...col,
            sortable: col.sortable !== undefined ? col.sortable : true
        }));
        this.localStorage.set(
            "tablecolumns_" + this.version + this.tableIdentifier,
            JSON.stringify(columnMapping)
        );
    } else {
        columnMapping = JSON.parse(columnMappingString);

        // The saved layout is a user preference (order, visibility), not schema. Reconcile
        // it against the columns the code ships now: a renamed or removed prop would
        // otherwise survive forever as a dead column (BUG-090's fix was invisible behind
        // exactly that), and a column added in code would never appear for anyone with a
        // saved layout. Additions land at the position the code declares for them.
        const currentProps = new Set(this.columns.map((c): string => c.prop));
        const savedProps = new Set(columnMapping.map((m: { prop: string }): string => m.prop));
        const reconciled = columnMapping.filter((m: { prop: string }): boolean => currentProps.has(m.prop));
        const additions = this.columns.filter((c): boolean => !savedProps.has(c.prop));
        if (reconciled.length !== columnMapping.length || additions.length > 0) {
            additions.forEach((col): void => {
                const codeIndex = this.columns.findIndex((c): boolean => c.prop === col.prop);
                reconciled.splice(Math.min(codeIndex, reconciled.length), 0, {
                    ...col,
                    sortable: col.sortable !== undefined ? col.sortable : true
                });
            });
            columnMapping = reconciled;
            this.localStorage.set(
                "tablecolumns_" + this.version + this.tableIdentifier,
                JSON.stringify(columnMapping)
            );
        }
    }

    columnMapping = columnMapping.map((m: any): any => ({
        ...m,
        name: oldColumns.find((f: any) => f.prop === m.prop)?.name,
        width: oldColumns.find((f: any) => f.prop === m.prop)?.width,
        sortable: oldColumns.find((f: any) => f.prop === m.prop)?.sortable ?? true,
        maxWidth: oldColumns.find((f: any) => f.prop === m.prop)?.maxWidth
    }));

    this.columnList = columnMapping.filter((c: any): boolean => !c.disabled);
    this.columnMapping = columnMapping;
  }

  toggleColumn(col: any) {
    let columnMappingString = this.localStorage.get(
      "tablecolumns_" + this.version + this.tableIdentifier
    );
    let columnMapping = JSON.parse(columnMappingString);
    columnMapping.forEach((c: any): void => {
      if (c.name == col.name) {
        if (c.disabled) {
          c.disabled = false;
        } else {
          c.disabled = true;
        }
      }
    });
    this.columnMapping = columnMapping;
    this.columnList = columnMapping.filter((c: any): boolean => !c.disabled);
    this.localStorage.set(
      "tablecolumns_" + this.version + this.tableIdentifier,
      JSON.stringify(columnMapping)
    );
  }

  setupSort() {
    const sortKey = "tablesort_" + this.version + this.tableIdentifier;
    const sort = this.localStorage.get(sortKey);
    if (!sort) {
      return;
    }
    const parsed = JSON.parse(sort);
    const persisted: { dir: string; prop: string }[] = Array.isArray(parsed) ? parsed : [];
    // Some grids assign [columns] only after their first fetch (ngAfterViewInit), so there
    // may be nothing to validate against yet — replay the sort untouched in that case;
    // filtering here would silently delete a valid saved sort on every visit.
    if (!this.columns) {
      if (persisted.length === 0) {
        return;
      }
      this.sortEvent.emit({ sorts: persisted });
      this.sorts = persisted;
      return;
    }
    // A persisted sort on a prop the code no longer defines would replay a dead sort on
    // every load (same trap as the saved column layout, see setupColumns).
    const sorts = persisted.filter(
      (s: { prop: string }): boolean => this.columns.some((c): boolean => c.prop === s.prop)
    );
    if (sorts.length === 0) {
      this.localStorage.unset(sortKey);
      return;
    }
    this.sortEvent.emit({ sorts });
    this.sorts = sorts;
  }

  onSort(event: any) {
    this.sortEvent.emit(event);
    //console.log("SETTING SORT LOCALSTORAGE", event.sorts);
    this.localStorage.set(
      "tablesort_" + this.version + this.tableIdentifier,
      JSON.stringify(event.sorts)
    );
    this.sorts = event.sorts;
  }

  onEdit(index: number): void {
    // @ts-ignore
    this.isEditable[index] = true;
    this.editedItemOriginalData[index] = { ...this.rows[index] };
  }

  // Edit action dispatch: if the action provides a callback (e.g. open a modal
  // or navigate), invoke it; otherwise fall back to the datatable's built-in
  // inline row editing.
  onEditClick(action: DatalistActionItem, row: any, rowIndex: number): void {
    if (action.callback || action.route) {
      this.onActionClick(action, row.id);
    } else {
      this.onEdit(rowIndex);
    }
  }

  onCancelEdit(index: number): void {
    // @ts-ignore
    this.isEditable[index] = false;
    this.rows[index] = this.editedItemOriginalData[index];
    this.rows = [...this.rows];
  }

  loaderVisibility(value: boolean) {
    this.loadingIndicator = value;
  }

  onCustomFields(val: any) {
    this.openCustomFieldsCallback.emit(val);
  }

  updateRows(data: any) {
    this.rows = data;
  }

  // Gates row-action visibility by DatalistActionItem.requiredPermission. Backward compatible:
  // an action with no requiredPermission is always shown. Fail-open on the permission set not
  // being loaded yet (PermissionService.isReady === false) — never hide before load, since the
  // API is the real enforcement point regardless of what this hides (dashboard-security rule #1).
  // Called from the template *ngIf on every CD cycle (the component is not OnPush); has()/isReady
  // are both synchronous BehaviorSubject reads, so this is cheap.
  public shouldShowAction(action: DatalistActionItem): boolean {
    if (!action.requiredPermission) {
      return true;
    }
    if (!this.permissionService.isReady) {
      return true;
    }
    return this.permissionService.has(action.requiredPermission);
  }

  /**
   * True when at least one row action would render for this user.
   *
   * Uses the same shouldShowAction() the cells use, so it inherits its fail-open behaviour: while
   * the permission set is still loading nothing is hidden, and the column stays put rather than
   * appearing a moment later.
   */
  public get hasVisibleActions(): boolean {
    return (this.actions || []).some((action: DatalistActionItem): boolean => this.shouldShowAction(action));
  }

  /**
   * The columns actually rendered: columnList, minus the actions column when no action would
   * show in it.
   *
   * A read-only user had every icon hidden by permission and was left with an empty column and
   * its header taking width on every grid. The cells were already gated; nothing was gating the
   * column itself.
   *
   * Memoised on purpose. This is read by *ngFor on every change-detection cycle and the component
   * is not OnPush, so returning a freshly filtered array each time would make ngx-datatable re-diff
   * its columns continuously. The cache is rebuilt only when columnList is reassigned (all seven
   * assignment sites replace the array rather than mutating it) or when action visibility flips,
   * which happens once, when the permission set finishes loading.
   *
   * columnMapping is deliberately untouched: it drives the show/hide chooser, whose reorder
   * controls address entries by index. Filtering it would break those. The consequence is that a
   * user with no actions still sees "Actions" listed in the chooser, where toggling it does
   * nothing — noise, but harmless next to breaking column reordering for everyone.
   */
  public get renderedColumnList(): any[] {
    const withActions: boolean = this.hasVisibleActions;
    if (this.columnList !== this.renderedFrom || withActions !== this.renderedWithActions) {
      this.renderedFrom = this.columnList;
      this.renderedWithActions = withActions;
      this.renderedColumns = withActions
        ? (this.columnList || [])
        : (this.columnList || []).filter((col: any): boolean => col.prop !== "actions");
    }
    return this.renderedColumns;
  }

  onActionClick(item: DatalistActionItem, id: any) {
    if (item.route) {
      this.router.navigate([item.route]);
    } else if (item.callback) {
      this.callbackEmit.emit(item.callback(id));
    }
  }

  saveData(row: any, rowIndex: any) {
    this.callbackOnUpdate.emit(row);
    // @ts-ignore
    this.isEditable[rowIndex] = false;
  }
}
